import { createContext, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { istEinstellungsPfad, istKontoPfad, istRecherche } from '../pages/einstellungen/seiten';
import { VERWALTUNG_PFAD } from '../config/verwaltungPfad';
import { istKiCockpitPfad } from '../administration/cockpit/firmenCockpit';
import { istFirmenPfad } from '../administration/organization/firmenLeiste';

// Bereichs-Ebene (Profil-Nachbau F-02 Rahmen D1, F-11 und F-19, Bennys Freigaben 14.10.2026; BAU-EINSTELLUNGEN.md B1.2,
// B1.3, B1.5 und B9.1): drei Ebenen übereinander in App.tsx. Die App bleibt gemountet und ist inert, solange darüber etwas
// offen ist. Darüber liegt eine Bereichsseite (BereichsSeite.tsx, als erste „Meine Einstellungen“), darüber das Pop-up der
// Einstellungen (Hilfe, Über, Sicht; die Firma ist seit PF-703 eine Bereichsseite). Die Herkunft steht in state.hintergrund:
// bei einer Bereichsseite der App-Ort, bei einem Pop-up die Seite darunter (Bereichsseite oder App). „Schließen“ (F-26, bis dahin
// „Zurück zum Chat“) führt zum App-Ort; die angefangene Eingabe bleibt, weil die App gemountet bleibt.

/** Dieselbe Form wie EinstellungsZustand in pages/einstellungen/EinstLink.tsx. */
export type EbenenZustand = { hintergrund?: Location } | null;
/** Die Bereiche der Ebene; 'firma' ist die Seite „Einstellungen der Firma“ (PF-703, F-11), weitere Bereiche ergänzen ihren Namen.
 *  'verwaltung' ist die Plattform-Verwaltung (PF-403-3, F-19, F-20), 'cockpit' das Firmen-Cockpit (PF-506, F-08), 'hilfe' die
 *  Seite „Hilfe und Feedback“ (PF-308-2, F-06). */
export type BereichsName = 'konto' | 'firma' | 'verwaltung' | 'cockpit' | 'hilfe';
export type Ebene = 'app' | 'bereich' | 'popup';

/** „Meine Einstellungen“ (B1.1): /einstellungen und die Adresse jeder persönlichen Seite, ohne Schrägstrich am Ende. Eine
 *  Regel für die Konto-Seite: istKontoPfad (seiten.ts, BAU B0, B9.1). */
export const istKontoSeite = (pfad: string) => istKontoPfad(pfad);

/** Profil-Nachbau F-06/F-19, BAU-HILFE.md 1.1 (PF-308-2): /hilfe und jeder Pfad darunter, ohne Schrägstrich am Ende, nicht
 *  /hilfeX. '/hilfe' wie HILFE_PFAD in components/hilfe/hilfeLeiste.ts; die Datei lädt Symbole aus lucide-react, hier stehen nur
 *  leichte Module. */
export const istHilfePfad = (pfad: string) => {
  const ohne = pfad.replace(/\/+$/, '');
  return ohne === '/hilfe' || ohne.startsWith('/hilfe/');
};

// Je Bereich eine Zeile: Name und die Regel, welche Adressen ihm gehören.
const BEREICHE: { name: BereichsName; passt: (pfad: string) => boolean }[] = [
  { name: 'konto', passt: istKontoSeite },
  // Profil-Nachbau F-11 (PF-703): die Firmen-Seite.
  { name: 'firma', passt: istFirmenPfad },
  // PF-403-3: /verwaltung und jeder Pfad darunter (die Unterpfade braucht PF-409), nicht /verwaltungx.
  { name: 'verwaltung', passt: pfad => pfad === VERWALTUNG_PFAD || pfad.startsWith(`${VERWALTUNG_PFAD}/`) },
  // Profil-Nachbau F-08 (BAU-COCKPIT.md 0.4): das Firmen-Cockpit /ki-cockpit.
  { name: 'cockpit', passt: istKiCockpitPfad },
  // PF-308-2: „Hilfe und Feedback“ (Unterseiten /hilfe/dokumente, /hilfe/chat, …).
  { name: 'hilfe', passt: istHilfePfad },
];

export const bereichVon = (pfad: string): BereichsName | null => BEREICHE.find(bereich => bereich.passt(pfad))?.name ?? null;
export const istBereichsPfad = (pfad: string) => bereichVon(pfad) !== null;
/** Jede andere Adresse unter /einstellungen bleibt das Pop-up. */
export const istPopupPfad = (pfad: string) => istEinstellungsPfad(pfad) && !istBereichsPfad(pfad);
export const ebeneVon = (pfad: string): Ebene => (istBereichsPfad(pfad) ? 'bereich' : istPopupPfad(pfad) ? 'popup' : 'app');
export const hintergrundVon = (ort: Location | null | undefined): Location | null => (ort?.state as EbenenZustand)?.hintergrund ?? null;
/** F-23 (BEFUNDE-FEHLERSUCHE-20261015 Punkt 3, F-19): Herkunft für „Schließen“ (F-26, bis dahin „Zurück zum Chat“) ist nur eine Seite mit der App-Leiste – die
 *  Startseite, die Recherche (/ris samt Unterpfaden) und „Vorlagen“. Anmelde-, Passwort-, Einladungs-, Bestätigungs- und
 *  SSO-Seiten und die Rechtstexte unter /rechtliches/ sind nie Herkunft; dann gilt die Startseite. */
export const istChatOrt = (pfad: string) => istRecherche(pfad) || pfad === '/vorlagen';

/** Der Ort jeder Ebene zur echten Adresse (B1.2). Die App rendert nie einen Bereichs- oder Pop-up-Pfad: ihre *-Route
 *  (<Navigate>) änderte sonst die echte Adresse. Fehlt der App-Ort (Neuladen, Aufruf von außen), gilt start. Seit F-23 zählt
 *  als Herkunft nur ein Ort mit istChatOrt. */
export function ebenenOrte(ort: Location, start: Location): { ebene: Ebene; app: Location; bereich: Location | null } {
  const ebene = ebeneVon(ort.pathname);
  let app: Location | null = ort, bereich: Location | null = null;
  if (ebene === 'bereich') { bereich = ort; app = hintergrundVon(ort); }
  else if (ebene === 'popup') {
    const unter = hintergrundVon(ort);
    if (unter && istBereichsPfad(unter.pathname)) { bereich = unter; app = hintergrundVon(unter); }
    else app = unter;
  }
  // F-23: auf der App-Ebene der Ort selbst; als Herkunft hinter einer Bereichsseite oder einem Pop-up nur ein Ort der App-Leiste.
  return { ebene, app: ebene === 'app' ? ort : app && istChatOrt(app.pathname) ? app : start, bereich };
}

/** Wie ein Link wechselt (B1.3, BAU-FIRMA.md 1.8): Hintergrund des Ziels, Ersetzen des Verlaufseintrags und ob die Wache
 *  der Ebene vor ungespeicherten Eingaben fragt. Die Ebene des Links ergibt sich aus von, dem Ort seiner eigenen
 *  <Routes location>. */
export type LinkRegel = { hintergrund: Location | null; replace: boolean; rueckfrage: boolean };
export function linkRegel(von: Location, zielPfad: string): LinkRegel {
  const ebene = ebeneVon(von.pathname), ziel = ebeneVon(zielPfad);
  // Aus der App: push, die aktuelle Ansicht wird der Hintergrund.
  if (ebene === 'app') return { hintergrund: von, replace: false, rueckfrage: false };
  // Aus Bereichsseite oder Pop-up in die App: ein gewöhnlicher Seitenwechsel ohne Hintergrund.
  if (ziel === 'app') return { hintergrund: null, replace: false, rueckfrage: true };
  if (ebene === 'bereich') {
    // Ein Pop-up über der Seite: die Seite bleibt darunter stehen, mit ihren Eingaben, und fragt deshalb nicht.
    if (ziel === 'popup') return { hintergrund: von, replace: false, rueckfrage: false };
    // Im selben Bereich ersetzt der Wechsel den Eintrag; ein anderer Bereich legt einen an. Der App-Ort bleibt.
    return { hintergrund: hintergrundVon(von), replace: bereichVon(zielPfad) === bereichVon(von.pathname), rueckfrage: true };
  }
  // Aus dem Pop-up: ein anderes Pop-up behält dessen Hintergrund; eine Bereichsseite bekommt den App-Ort.
  const h = hintergrundVon(von);
  if (ziel === 'popup') return { hintergrund: h, replace: true, rueckfrage: true };
  return { hintergrund: h && istBereichsPfad(h.pathname) ? hintergrundVon(h) : h, replace: true, rueckfrage: true };
}

/** Ziel von „Schließen“ (F-26, bis dahin „Zurück zum Chat“; B1.5): der App-Ort im Hintergrund samt Abfrage und Anker, sonst die Startseite. PF-403-3
 *  (F-19): Fehlt der Hintergrund (ein Seitenwechsel ohne state, etwa in der Plattform-Verwaltung), gilt der gemerkte App-Ort.
 *  Seit F-23 nur ein Ort mit istChatOrt, sonst die Startseite. */
export function zurueckZiel(ort: Location, gemerkt?: Location | null): string {
  const h = hintergrundVon(ort);
  if (h && istChatOrt(h.pathname)) return h.pathname + h.search + h.hash;
  return gemerkt && istChatOrt(gemerkt.pathname) ? gemerkt.pathname + gemerkt.search + gemerkt.hash : '/';
}

/** PF-403-3 (F-19, „Die Herkunft merkt sich die App beim Öffnen des Bereichs“): der App-Ort, der hinter der Ebene wirklich
 *  steht. App.tsx setzt ihn; ohne Anbieter null, dann gilt allein der Hintergrund. */
export const HerkunftKontext = createContext<Location | null>(null);

/** „Schließen“ (F-26, bis dahin „Zurück zum Chat“) und Escape (F-19): ein Seitenwechsel (push) zum App-Ort, kein navigate(-1), damit das Ziel auch nach
 *  Umwegen über Pop-ups stimmt und Browser-Zurück wieder zur Seite führt. Ohne state: Den alten mitzugeben schickte
 *  pages/RISAssistant.tsx einen researchPrompt erneut ab; die Eingabe bleibt, weil die App gemountet bleibt. */
export function useZurueckZumChat(): { ziel: string; zurueck: () => void } {
  const ziel = zurueckZiel(useLocation(), useContext(HerkunftKontext));
  const navigate = useNavigate();
  const zurueck = useCallback(() => navigate(ziel), [navigate, ziel]);
  return { ziel, zurueck };
}

/** F-23 (Nutzerblick Firma H-14, Betreiber H-19, Mitglied H-15): Titel des Browserfensters einer Bereichsseite, „{Seite} ·
 *  {Bereich} · {Produkt}“; ohne aktiven Eintrag oder wenn die Seite wie der Bereich heißt, „{Bereich} · {Produkt}“. */
export const fensterTitel = (seite: string | null | undefined, bereich: string, produkt: string) =>
  [seite && seite !== bereich ? seite : null, bereich, produkt].filter(Boolean).join(' · ');
